import type { Dispatch } from "react";
import type { EditorAction } from "../../editor/reducers";
import { useState } from "react";
import { ListItemFormField } from "../../attributes/list-item/ListItemFormField";
import { TextFormField } from "../../attributes/text/TextFormField";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { assertTypeName } from "../../client/guards";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { Language } from "../../rich-text/extension-language";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  useListItemRegionEditorSuspenseQuery,
  useUpdateListItemRegionMutation,
} from "../../client/generated";

type Props = {
  listItemAttributeId: string;
  textAttributeId: string;
  dispatch: Dispatch<EditorAction>;
};

export function ListItemRegionEditor({
  listItemAttributeId,
  textAttributeId,
  dispatch,
}: Props) {
  const { data } = useListItemRegionEditorSuspenseQuery({
    variables: {
      listItemAttributeId,
      textAttributeId,
    },
  });

  const { listItemAttr, textAttr } = data;
  assertTypeName(listItemAttr?.__typename === "ListItemAttribute");
  assertTypeName(textAttr?.__typename === "TextAttribute");

  const [updateRegion] = useUpdateListItemRegionMutation();
  const [ordinal, setOrdinal] = useState(listItemAttr.ordinal);
  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
      ],
      content: textAttr.text,
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editor) return false;

    const result = await updateRegion({
      variables: {
        listItemAttributeId,
        textAttributeId,
        ordinal,
        text: editor.getHTML(),
      },
    });

    return (
      !result.errors &&
      !result.data?.updateListItemAttribute.userErrors?.length &&
      !result.data?.updateTextAttribute.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <ListItemFormField
        value={ordinal}
        onChange={(event) => setOrdinal(parseInt(event.target.value))}
      />
      <TextFormField fragment={textAttr.region} editor={editor} />
    </RegionUpdateForm>
  );
}
